import ModalComponent from "../../../components/modals";
import LoadingFeedback, { useLoadingState } from "../../../components/loadingFeedback";
import LaddaButton from "../../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import SimpleTable from "../../../components/tables/simpleTable";
import { useState } from "react";
import { icons } from "../../../constants";
import SimpleSelect from "../../../components/selects/simpleSelect";
import { months, monthsWithAll } from "../../../constants/selectData";
import { useEffect } from "react";
import FormTextField from "../../../components/textField/formTextField";
import { hasAnyRoleUtil } from "../../../services/roleService";
import { useSelector } from "react-redux";
import {
  callApiAsync,
  getDowntimeMonthlyKpi,
  getRegisterIndicatorAnalysisByTime,
  getServiceOrderAnalysisYearsByTableId,
  getTableDowntimeIndicator,
  getTableIndicatorAnalysisByTime,
  saveAnalysisDowntimeIndicator,
  saveRegisterIndicatorAnalysisByTime
} from "../../../services/apiService";
import { analisisTableType } from "../../../constants/enums";
import {
  getCurrentMonth,
  getCurrentYear,
  getMonthName
} from "../../../utils/dateUtils";
import ToastNotification from "../../../components/toastNotification";
import { BIOMEDICAL_DASHBOARD_ANALYSIS_EDIT_ROLES } from "../../../constants/biomedicalRoles";

const TimeAnalisisModal = ({
  open,
  handleClose,
  activityId,
  activityName,
  alternateView
}) => {
  const [dataTable, setDataTable] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dataFilteredTable, setFilteredDataTable] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [yearFilter, setYearFilter] = useState([]);
  const [monthFilter, setMonthFilter] = useState([]);

  const [toastConfig, setToastConfig] = useState({
    severity: "",
    summary: "",
    detail: ""
  });
  const [saving, setSaving] = useState(false);

  const userData = useSelector((state) => state.userData.userData);

  const isEditIndicator = hasAnyRoleUtil(
    BIOMEDICAL_DASHBOARD_ANALYSIS_EDIT_ROLES,
    userData.roleList
  );

  const analisisColumns = [
    {
      field: "month_year",
      header: "Mes/Año",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {getMonthName(rowData.month) + " - " + rowData.year}
        </p>
      ),
      columnWidth: "115px"
    },
    { field: "real_value", header: "Real", columnWidth: "180px" },
    { field: "goal_value", header: "Meta", columnWidth: "180px" },
    {
      field: "comment",
      columnType: "tooltip",
      columnWidth: "450px",
      header: "Analisis Indicador"
    },
    ...(isEditIndicator
      ? [
          {
            field: "actions",
            header: "",
            columnType: "actionButtons"
          }
        ]
      : [])
  ];

  const ToolTipDescription = (text) => {
    return (
      <div>
        <p className="tooltip-title">Descripción:</p>
        <div>
          <p className="tooltip-text">{text}</p>
        </div>
      </div>
    );
  };

  const {
    control,
    getValues,
    setValue,
    reset,
    watch,
    formState: { errors, isValid }
  } = useForm({
    defaultValues: {
      year: getCurrentYear(),
      monthFilter: getCurrentMonth()
    }
  });

  const watchYear = watch("year");
  const watchMonth = watch("monthFilter");

  const buildYearOptions = (yearsFromApi) => {
    const currentYear = getCurrentYear();
    const minYear = Math.min(2025, currentYear - 1);
    const yearSet = new Set([
      currentYear,
      currentYear - 1,
      minYear,
      ...(yearsFromApi || [])
    ]);
    const sorted = Array.from(yearSet).sort((a, b) => a - b);
    return sorted.map((y) => ({ name: String(y), value: y }));
  };

  const [loadingTable, setLoadingTable] = useLoadingState();
  const [loadingValues, setLoadingValues] = useLoadingState();
  const getTableData = async (year, month) => {
    if (alternateView) {
      await callApiAsync(
        () => getTableDowntimeIndicator(),
        (data) => {
          setDataTable(data || []);
          setFilteredDataTable(data || []);
          getAvailablePastMonths();
          setYearFilter(
            Array.from(new Set((data || []).map((item) => item.year))).map(
              (y) => ({ name: String(y), value: y })
            )
          );
        },
        setLoadingTable
      );
      return;
    }

    const effectiveYear = year != null ? year : getCurrentYear();
    const effectiveMonth =
      month != null && month !== "" ? month : undefined;

    await callApiAsync(
      () =>
        getTableIndicatorAnalysisByTime(activityId ? activityId : 0, {
          year: effectiveYear,
          ...(effectiveMonth != null ? { month: effectiveMonth } : {})
        }),
      (responseData) => {
        const list = Array.isArray(responseData) ? responseData : [];
        setDataTable(list);
        setFilteredDataTable(list);
      },
      setLoadingTable
    );
  };

  const getAvailablePastMonths = () => {
    const list = getMonthsAvailableForAdd();
    setMonthFilter(list);
  };

  /** Meses pasados que aún no tienen análisis (para no mostrar duplicados al agregar) */
  const getMonthsAvailableForAdd = () => {
    const currentYear = getCurrentYear();
    const currentMonth = getCurrentMonth();
    const registeredMonths = new Set(
      (dataTable || [])
        .filter((item) => item.year === currentYear)
        .map((item) => item.month)
    );
    return months.filter(
      (m) => m.value < currentMonth && !registeredMonths.has(m.value)
    );
  };

  const getSelectData = async (monthData) => {
    if (alternateView) {
      await callApiAsync(
        () => getDowntimeMonthlyKpi(monthData),
        (data) => {
          setValue("goal_value", data?.goal_value);
          setValue("real_value", data?.real_value);
        },
        setLoadingValues
      );
      return;
    }
    await callApiAsync(
      () => getRegisterIndicatorAnalysisByTime(monthData, activityId),
      (data) => {
        setValue("goal_value", data?.goal_value);
        setValue("real_value", data?.averageDuration);
      },
      setLoadingValues
    );
  };

  const applyFilterFromForm = () => {
    const filters = getValues();
    if (!alternateView) {
      const year = filters.year != null ? filters.year : getCurrentYear();
      const isEmptyMonth =
        filters.monthFilter === "" ||
        filters.monthFilter === "all" ||
        filters.monthFilter == null;
      const month = isEmptyMonth ? undefined : filters.monthFilter;
      getTableData(year, month);
    } else {
      const filtered = dataTable.filter((item) => {
        const isEmptyMonth =
          filters.monthFilter === "" ||
          filters.monthFilter === "all" ||
          filters.monthFilter == null;
        const matchMonth = isEmptyMonth
          ? true
          : item.month === filters.monthFilter;
        const matchYear = filters.year ? item.year === filters.year : true;
        return matchMonth && matchYear;
      });
      setFilteredDataTable(filtered);
    }
  };

  const handleChangeMonth = () => {
    const monthData = getValues().month;
    getSelectData(monthData);
  };

  const handleClearFilters = () => {
    const defaultYear = getCurrentYear();
    reset({ year: defaultYear, monthFilter: "all" });
    if (!alternateView) {
      getTableData(defaultYear, undefined);
    } else {
      setFilteredDataTable(dataTable);
    }
  };

  const handleOpenAddModal = (row) => {
    setSelectedRow(row);
    setAddModal(true);
    getAvailablePastMonths();
    setValue("id", row.id);
    setValue("month", row.month);
    setValue("real_value", row.real_value);
    setValue("goal_value", row.goal_value);
    setValue("comment", row.comment);
  };

  const handleCloseAdd = () => {
    setValue("id", null);
    setValue("month", null);
    setValue("real_value", "");
    setValue("goal_value", "");
    setValue("comment", "");
    setAddModal(false);
  };

  const buttonTable = () => {
    if (isEditIndicator)
      return [
        {
          type: "icon",
          icon: icons.editBlue,
          onClick: (data) => handleOpenAddModal(data)
        }
      ];
    else return [];
  };

  const handleSave = async () => {
    if (saving) return;
    const filters = getValues();
    const currentYear = getCurrentYear();
    const selectedMonth = filters.month;

    if (alternateView) {
      const isDuplicate = dataTable.some((item) => {
        const isSameMonth = item.month === selectedMonth;
        const isSameYear = item.year === currentYear;
        const isDifferentRow = !selectedRow || item.id !== selectedRow.id;
        return isSameMonth && isSameYear && isDifferentRow;
      });
      if (isDuplicate) {
        setToastConfig({
          severity: "warn",
          summary: "Error al validar.",
          detail: "Ya existe un análisis registrado para este mes y año."
        });
        setShowToast(true);
        return;
      }
      const dataToSend = {
        id: selectedRow ? selectedRow.id : 0,
        tableType: analisisTableType.timeAnalisis,
        activityId: activityId ? activityId : null,
        ...filters
      };
      setSaving(true);
      try {
        await callApiAsync(
          () => saveAnalysisDowntimeIndicator(dataToSend),
          () => {
            getTableData();
            setValue("id", null);
            setValue("month", null);
            setValue("real_value", "");
            setValue("goal_value", "");
            setValue("comment", "");
            handleCloseAdd();
          }
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    const isDuplicate = dataTable.some((item) => {
      const isSameMonth = item.month === selectedMonth;
      const isSameYear = item.year === currentYear;
      const isDifferentRow = !selectedRow || item.id !== selectedRow.id;
      return isSameMonth && isSameYear && isDifferentRow;
    });

    if (isDuplicate) {
      setToastConfig({
        severity: "warn",
        summary: "Error al validar.",
        detail: "Ya existe un análisis registrado para este mes y año."
      });
      setShowToast(true);
      return;
    }

    const dataToSend = {
      id: selectedRow ? selectedRow.id : 0,
      tableType: analisisTableType.timeAnalisis,
      activityId: activityId ? activityId : null,
      ...filters
    };
    setSaving(true);
    try {
      await callApiAsync(
        () => saveRegisterIndicatorAnalysisByTime(dataToSend),
        () => {
          handleCloseAdd();
          const { year, monthFilter } = getValues();
          getTableData(year, monthFilter);
          setValue("id", null);
          setValue("month", null);
          setValue("real_value", "");
          setValue("goal_value", "");
          setValue("comment", "");
        }
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    if (alternateView) {
      getTableData();
      return;
    }

    const defaultYear = getCurrentYear();
    const defaultMonth = getCurrentMonth();
    setYearFilter(buildYearOptions([defaultYear]));

    callApiAsync(
      () =>
        getServiceOrderAnalysisYearsByTableId(
          analisisTableType.timeAnalisis,
          { activityId: activityId || 0 }
        ),
      (response) => {
        const years = response?.years || [];
        setYearFilter(buildYearOptions(years));
        reset({ year: defaultYear, monthFilter: defaultMonth });
      },
      setLoadingTable
    );
  }, [open]);

  useEffect(() => {
    if (!open || alternateView) return;
    const y = watchYear != null ? Number(watchYear) : getCurrentYear();
    if (!Number.isInteger(y)) return;
    const isEmptyMonth =
      watchMonth === "" || watchMonth === "all" || watchMonth == null;
    const m = isEmptyMonth ? undefined : Number(watchMonth);
    if (m !== undefined && !Number.isInteger(m)) return;
    getTableData(y, m);
  }, [open, alternateView, watchYear, watchMonth]);

  useEffect(() => {
    if (!open || !alternateView) return;
    applyFilterFromForm();
  }, [open, alternateView, watchYear, watchMonth]);

  return (
    <div>
      <ModalComponent
        open={open}
        width="1100px"
        maxWidth="calc(100vw - 32px)"
        handleClose={handleClose}
        title={""}
        actions={<div className="HContainer j-center w-complete"></div>}
      >
        <div className="IHContainer j-space-between">
          <h1 className="title-bio-details-chard">
            {`${activityName ? activityName : "Todas las actividades"}`}
          </h1>
          {isEditIndicator &&
            (activityId ? (
              <LaddaButton
                height={"35px"}
                variant="contained"
                type="submit"
                onClick={() => handleOpenAddModal(null)}
              >
                <img
                  className="btn-icon mr-5"
                  src={icons.plusIcon}
                  alt="icon"
                />{" "}
                Agregar
              </LaddaButton>
            ) : (
              <></>
            ))}
        </div>
        <div className="filter-container">
          <form>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={3}>
                <div className="mid-filter-margin">
                  <SimpleSelect
                    name="monthFilter"
                    control={control}
                    label="Mes"
                    options={monthsWithAll}
                    margin={"0"}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={3}>
                <SimpleSelect
                  name="year"
                  control={control}
                  label="Año"
                  options={yearFilter}
                  margin={"0"}
                />
              </Grid>
              <Grid item xs />
              <Grid item xs={12} md={1.5}>
                <div className="IHContainer j-end">
                  <LaddaButton
                    variant="contained"
                    onClick={handleClearFilters}
                    margin={"0"}
                    width={"52px"}
                  >
                    <img
                      className="btn-icon"
                      src={icons.clearFilterWhite}
                      alt="icon"
                    />
                  </LaddaButton>
                </div>
              </Grid>
            </Grid>
          </form>
        </div>

        <SimpleTable
          loading={loadingTable}
          data={dataFilteredTable}
          columns={analisisColumns}
          buttons={buttonTable()}
          tooltipFragment={ToolTipDescription}
          isPaginated
          rows={10}
        />
      </ModalComponent>

      <ModalComponent
        open={addModal}
        handleClose={handleCloseAdd}
        title={""}
        actions={<div className="HContainer j-center w-complete"></div>}
      >
        <br />
        <LoadingFeedback loading={loadingValues} variant="spinner" />
        <form>
          <Grid justifyContent="flex-end" container spacing={1}>
            <Grid item xs={12} md={4}>
              <p className="info-label">Fecha:</p>
              <SimpleSelect
                name="month"
                control={control}
                label=""
                options={selectedRow ? months : getMonthsAvailableForAdd()}
                margin={"0"}
                onChange={() => handleChangeMonth()}
                disabled={selectedRow ? true : false}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <p className="info-label">Real:</p>

              <FormTextField
                name={"real_value"}
                control={control}
                error={errors.engineeringReport}
                disabled={true}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <p className="info-label">Meta:</p>

              <FormTextField
                name={"goal_value"}
                control={control}
                error={errors.engineeringReport}
                disabled={true}
                margin={"0"}
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <p className="info-label">Analisis Indicador:</p>
              <FormTextField
                name={"comment"}
                control={control}
                isTextArea={true}
                error={errors.engineeringReport}
                maxLength={1000}
                rows={3}
                margin={"0"}
              />
            </Grid>
          </Grid>

          <div
            className="j-center mt-20"
            style={{ display: "flex", gap: "10px" }}
          >
            <LaddaButton
              variant="contained"
              onClick={() => handleSave()}
              disabled={saving}
            >
              Guardar
            </LaddaButton>
          </div>
        </form>
      </ModalComponent>

      <ToastNotification
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
        showToast={showToast}
        setShowToast={setShowToast}
      />
    </div>
  );
};

export default TimeAnalisisModal;
